'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { customerListMachine } from './machines/customerListMachine'
import type { Customer, CustomerFilters, CustomerStats } from './types'

export type CustomerListMachineApi = ReturnType<typeof useMachine<typeof customerListMachine>>

const CustomerListMachineContext = createContext<CustomerListMachineApi | null>(null)

export function CustomerListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(customerListMachine)
  return (
    <CustomerListMachineContext.Provider value={api}>
      {children}
    </CustomerListMachineContext.Provider>
  )
}

export function useCustomerListMachine() {
  const ctx = useContext(CustomerListMachineContext)
  if (!ctx) {
    throw new Error('useCustomerListMachine must be used within CustomerListMachineProvider')
  }
  return ctx
}

export function useCustomerListMachineOptional() {
  return useContext(CustomerListMachineContext)
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useCustomerListStoreCompat() {
  const machineApi = useCustomerListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    return {
      customers: ctx.customers,
      selectedCustomer: ctx.selectedCustomer,
      totalCount: ctx.totalCount,
      stats: ctx.stats,
      filters: ctx.filters,
      isLoading: ctx.isLoading,
      setCustomers: (customers: Customer[], total: number) =>
        send({ type: 'SET_CUSTOMERS', customers, total }),
      setSelectedCustomer: (customer: Customer | null) =>
        send({ type: 'SET_SELECTED_CUSTOMER', customer }),
      addCustomer: (customer: Customer) => send({ type: 'ADD_CUSTOMER', customer }),
      updateCustomer: (id: string, updates: Partial<Customer>) =>
        send({ type: 'UPDATE_CUSTOMER', id, updates }),
      removeCustomer: (id: string) => send({ type: 'REMOVE_CUSTOMER', id }),
      setStats: (stats: CustomerStats) => send({ type: 'SET_STATS', stats }),
      setFilters: (filters: Partial<CustomerFilters>) => send({ type: 'SET_FILTERS', filters }),
      resetFilters: () => send({ type: 'RESET_FILTERS' }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    }
  }, [machineApi])
}

export function useCustomerActions() {
  const machineApi = useCustomerListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setCustomers: (customers: Customer[], total: number) =>
      send({ type: 'SET_CUSTOMERS', customers, total }),
    setSelectedCustomer: (customer: Customer | null) =>
      send({ type: 'SET_SELECTED_CUSTOMER', customer }),
    addCustomer: (customer: Customer) => send({ type: 'ADD_CUSTOMER', customer }),
    updateCustomer: (id: string, updates: Partial<Customer>) =>
      send({ type: 'UPDATE_CUSTOMER', id, updates }),
    removeCustomer: (id: string) => send({ type: 'REMOVE_CUSTOMER', id }),
    setStats: (stats: CustomerStats) => send({ type: 'SET_STATS', stats }),
    setFilters: (filters: Partial<CustomerFilters>) => send({ type: 'SET_FILTERS', filters }),
    resetFilters: () => send({ type: 'RESET_FILTERS' }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

export function useCustomers() {
  return useCustomerListStoreCompat().customers
}
export function useSelectedCustomer() {
  return useCustomerListStoreCompat().selectedCustomer
}
export function useCustomerFilters() {
  return useCustomerListStoreCompat().filters
}
export function useCustomerStats() {
  return useCustomerListStoreCompat().stats
}
